/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    ApplicationRef,
    ComponentFactoryResolver,
    Injectable,
    Injector,
    Type
} from '@angular/core';
import { Observable } from 'rxjs';
import {FcAgUtilsService} from '../fc-ag-services/fc-ag-utils.service';
import { FcResponsiveAgDialogHolderComponent } from './fc-responsive-ag-dialog-holder.component';
import { FcResponsiveAgDialogComponent } from './fc-responsive-ag-dialog.component';
import { FcResponsiveAgDialogSimpleMessageComponent } from './fc-responsive-ag-dialog-simple-message.component';
import { FcResponsiveAgDialogOptionsOverrides } from './fc-responsive-ag-dialog-options';

/**
 * FcResponsiveAgDialogService.
 */
@Injectable()
export class FcResponsiveAgDialogService {
    
    private modalHolderComponent: FcResponsiveAgDialogHolderComponent;
    
    /**
     * Constructor for service.
     *
     * @param resolver Component Factory Resolver.
     * @param applicationRef Component Reference.
     * @param injector Component Injector.
     * @param fcAgUtilsService FcAgUtilsService.
     */
    constructor(
        private resolver: ComponentFactoryResolver,
        private applicationRef: ApplicationRef,
        private injector: Injector,
        private fcAgUtilsService: FcAgUtilsService
    ) {}

    /**
     * Adds modal function.
     *
     * @param component Dialog component instance.
     * @param data Dialog component data.
     * @param options Dialog component options.
     * @returns Observable Observable object.
     */
    addModal(
        component: Type<FcResponsiveAgDialogComponent>,
        data?: any,
        options?: FcResponsiveAgDialogOptionsOverrides
    ): Observable<any> {
        if (!this.modalHolderComponent) {
            this.modalHolderComponent =
                this.fcAgUtilsService.createHolderComponent(
                    FcResponsiveAgDialogHolderComponent,
                    this.resolver, this.applicationRef, this.injector);
        }
        return this.modalHolderComponent.addModal(component, data, options);
    }

    /**
     * Hides and removes modal from DOM, resolves promise when fully removed.
     *
     * @param component Component instance.
     */
    removeModal(component: FcResponsiveAgDialogComponent): void {
        if (this.modalHolderComponent) {
            this.modalHolderComponent.removeModal(component);
        }
    }

    /**
     * Closes all modals, resolves promise when they're fully removed.
     *
     * @returns Promise Project object.
     */
    removeAll(): Promise<{}> {
        if (!this.modalHolderComponent) {
            return Promise.resolve({});
        }
        return this.modalHolderComponent.removeAllModals();
    }

    /**
     * Show simple message function.
     * 
     * @param data Dialog data.
     * @param options Dialog component options.
     * @returns Observable Observable object.
     */
    showSimpleMessageDialog(data: any, options?: FcResponsiveAgDialogOptionsOverrides): Observable<any> {
        return this.addModal(FcResponsiveAgDialogSimpleMessageComponent, data, options);
    }

    /**
     * Show simple error message function.
     * 
     * @param message Dialog error message.
     * @returns Observable Observable object.
     */
    showSimpleErrorMessageDialog(message: string): Observable<any> {
        return this.addModal(FcResponsiveAgDialogSimpleMessageComponent, {
            iconClass: 'error-icon',
            title: 'Error',
            message: message
        });
    }
}